import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { format, formatDistanceToNow } from 'date-fns';
import type { CommentResponse } from '@erp/rpc';
import { CommentBody } from './CommentBody';
import { NoteDeleteDialog } from './NoteDeleteDialog';
import { NoteEditForm } from './NoteEditForm';
import { NoteRowActions } from './NoteRowActions';
import { noteRowModifyFlags } from './commentIsEditableBy';
import { persistNoteChange } from './noteRowPersist';

/** What one note row reads; a `CommentResponse` fits, and so do lighter DTOs. */
export type NoteRowComment = Pick<
  CommentResponse,
  | 'id'
  | 'authorType'
  | 'authorUserId'
  | 'body'
  | 'createdAt'
  | 'visibility'
  | 'editedAt'
>;

function noteAuthorLabel({
  comment,
  agentName,
  currentUserId,
  userNameById,
  gt,
}: {
  comment: NoteRowComment;
  agentName?: string;
  currentUserId?: string;
  userNameById?: Map<string, string>;
  gt: ReturnType<typeof useGT>;
}): string {
  if (comment.authorType === 'agent') {
    return (
      agentName ??
      gt('Agent', { context: 'note author when agent name unknown' })
    );
  }
  if (comment.authorType === 'system') {
    return gt('System', { context: 'note author for system comments' });
  }
  if (comment.authorUserId && comment.authorUserId === currentUserId) {
    return gt('You', { context: 'note author for the current user' });
  }
  if (comment.authorUserId) {
    return (
      userNameById?.get(comment.authorUserId) ??
      gt('User', { context: 'note author when user name is unknown' })
    );
  }
  return gt('User', { context: 'note author when user id is missing' });
}

/**
 * One note in a thread: author, relative time, an "edited" tag, and edit /
 * delete on the viewer's own notes when the host passes the callbacks.
 * Shared by `CommentsCard` and the Item Master review's `CommentThread`.
 */
export function NoteRow({
  comment,
  agentName,
  currentUserId,
  userNameById,
  onUpdate,
  onDelete,
}: {
  comment: NoteRowComment;
  agentName?: string;
  currentUserId?: string;
  userNameById?: Map<string, string>;
  onUpdate?: (commentId: string, body: string) => Promise<void>;
  onDelete?: (commentId: string) => Promise<void>;
}) {
  const gt = useGT();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { showEdit, showDelete } = noteRowModifyFlags({
    comment,
    currentUserId,
    canUpdate: onUpdate !== undefined,
    canDelete: onDelete !== undefined,
  });
  const author = noteAuthorLabel({
    comment,
    agentName,
    currentUserId,
    userNameById,
    gt,
  });
  const when = formatDistanceToNow(new Date(comment.createdAt), {
    addSuffix: true,
  });

  async function handleSave(body: string): Promise<void> {
    if (!onUpdate) return;
    setSaving(true);
    await persistNoteChange({
      run: () => onUpdate(comment.id, body.trim()),
      onSuccess: () => setEditing(false),
      successMessage: gt('Comment updated', {
        context: 'toast after editing a comment on a record',
      }),
      fallbackError: gt('Failed to update comment', {
        context: 'toast after editing a comment failed',
      }),
    });
    setSaving(false);
  }

  async function handleDelete(): Promise<void> {
    if (!onDelete) return;
    setDeleting(true);
    await persistNoteChange({
      run: () => onDelete(comment.id),
      onSuccess: () => setDeleteOpen(false),
      successMessage: gt('Comment deleted', {
        context: 'toast after deleting a comment on a record',
      }),
      fallbackError: gt('Failed to delete comment', {
        context: 'toast after deleting a comment failed',
      }),
    });
    setDeleting(false);
  }

  return (
    <li className="rounded-md bg-stone-50 px-2 py-1.5">
      <div className="flex items-baseline justify-between gap-2 text-[10px] text-muted-foreground">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="truncate font-medium text-stone-700">{author}</span>
          {comment.visibility === 'internal' && (
            <span className="shrink-0 rounded bg-amber-100 px-1 text-[9px] font-semibold tracking-wide text-amber-800 uppercase">
              <T context="chip on a comment only the triage team can see">
                Internal
              </T>
            </span>
          )}
          {comment.editedAt && (
            <span
              className="shrink-0 italic"
              title={format(new Date(comment.editedAt), 'MMM d, yyyy · h:mm a')}
            >
              <T context="tag on a comment its author changed after posting">
                edited
              </T>
            </span>
          )}
        </span>
        <span className="flex shrink-0 items-center gap-1">
          {!editing && (
            <NoteRowActions
              showEdit={showEdit}
              showDelete={showDelete}
              busy={saving || deleting}
              editLabel={gt('Edit note', {
                context: 'button that starts editing a discussion note',
              })}
              deleteLabel={gt('Delete note', {
                context: 'button that starts deleting a discussion note',
              })}
              onEdit={() => setEditing(true)}
              onDelete={() => setDeleteOpen(true)}
            />
          )}
          <time
            className="tabular-nums"
            dateTime={new Date(comment.createdAt).toISOString()}
            title={format(new Date(comment.createdAt), 'MMM d, yyyy · h:mm a')}
          >
            {when}
          </time>
        </span>
      </div>
      {editing ? (
        <NoteEditForm
          initialBody={comment.body}
          isSaving={saving}
          onSave={handleSave}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <p className="mt-0.5 text-xs leading-snug whitespace-pre-wrap text-stone-800">
          <CommentBody body={comment.body} userNameById={userNameById} />
        </p>
      )}
      <NoteDeleteDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => void handleDelete()}
        message={gt('This note will be removed from the discussion.', {
          context: 'confirm dialog body when deleting a discussion note',
        })}
        loading={deleting}
      />
    </li>
  );
}
